// Вкладка «Причины»: admin CRUD GET/POST/PUT/DELETE /api/admin/rejection-reasons (V16 W11).
// Канон TeamPage: Table, Dialog+RHF+zod, 409 → extractApiErrorMessage; строка при 409 жива.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import {
  adminDictionariesApi,
  type AdminRejectionReason,
} from '@/api/adminDictionaries';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** staleTime admin-таблицы причин (инвалидация по мутациям). */
const REASONS_ADMIN_STALE_TIME_MS = 5 * 60 * 1000;

/** Zod-схема формы причины (лимит имени — канон BE 200). */
const reasonSchema = z.object({
  name: z.string().trim().min(1, 'Название обязательно').max(200, 'Слишком длинное название'),
  active: z.boolean(),
});

/** Значения формы причины. */
type ReasonFormValues = z.infer<typeof reasonSchema>;

/** Вкладка справочника причин отказа: таблица, создание, правка, удаление. */
export function RejectionReasonsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminRejectionReason | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'rejection-reasons'],
    queryFn: () => adminDictionariesApi.listRejectionReasons(),
    staleTime: REASONS_ADMIN_STALE_TIME_MS,
  });

  const invalidateReasons = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'rejection-reasons'] });
    void queryClient.invalidateQueries({ queryKey: ['rejection-reasons'] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: { name: string }) => adminDictionariesApi.createRejectionReason(payload),
    onSuccess: () => {
      setPageError(null);
      setDialogOpen(false);
      invalidateReasons();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name, active }: { id: string; name: string; active: boolean }) =>
      adminDictionariesApi.updateRejectionReason(id, { name, active }),
    onSuccess: () => {
      setPageError(null);
      setDialogOpen(false);
      invalidateReasons();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminDictionariesApi.deleteRejectionReason(id),
    onSuccess: () => {
      setPageError(null);
      invalidateReasons();
    },
    onError: (error) => {
      setPageError(extractApiErrorMessage(error, t('dict.reasons.deleteError')));
    },
  });

  const { control, handleSubmit, reset } = useForm<ReasonFormValues>({
    resolver: zodResolver(reasonSchema),
    defaultValues: { name: '', active: true },
  });

  const openCreate = (): void => {
    setEditing(null);
    reset({ name: '', active: true });
    createMutation.reset();
    updateMutation.reset();
    setDialogOpen(true);
  };

  const openEdit = (reason: AdminRejectionReason): void => {
    setEditing(reason);
    reset({ name: reason.name, active: reason.active });
    createMutation.reset();
    updateMutation.reset();
    setDialogOpen(true);
  };

  const onSubmit: SubmitHandler<ReasonFormValues> = (values) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, name: values.name, active: values.active });
    } else {
      createMutation.mutate({ name: values.name });
    }
  };

  const savePending = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error ?? updateMutation.error;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('dict.reasons.create')}
        </Button>
      </Stack>
      {query.isLoading ? (
        <LoadingSpinner label={t('common.loading')} />
      ) : query.isError ? (
        <EmptyState
          title={t('dict.reasons.loadError')}
          description={
            query.error instanceof Error ? query.error.message : t('dict.reasons.loadErrorDesc')
          }
        />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState title={t('dict.reasons.empty')} description={t('dict.reasons.emptyDesc')} />
      ) : (
        <Paper>
          {pageError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {pageError}
            </Alert>
          ) : null}
          <TableContainer>
            <Table size="medium" aria-label={t('dict.tabs.reasons')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('dict.reasons.name')}</TableCell>
                  <TableCell>{t('dict.columns.status')}</TableCell>
                  <TableCell>{t('dict.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.map((reason) => (
                  <TableRow key={reason.id} hover>
                    <TableCell>{reason.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(reason.active ? 'dict.status.active' : 'dict.status.inactive')}
                        color={reason.active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.edit')}
                            onClick={() => openEdit(reason)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            aria-label={t('common.delete')}
                            disabled={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate(reason.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? t('dict.reasons.editTitle') : t('dict.reasons.createTitle')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {saveError ? (
                <Alert severity="error">
                  {extractApiErrorMessage(saveError, t('dict.reasons.saveError'))}
                </Alert>
              ) : null}
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('dict.reasons.name')}
                    required
                    fullWidth
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              {editing ? (
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(event) => field.onChange(event.target.checked)}
                        />
                      }
                      label={t('dict.status.active')}
                    />
                  )}
                />
              ) : null}
            </Stack>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" variant="contained" disabled={savePending}>
                {editing ? t('common.save') : t('common.create')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
